import { useEffect, useState } from 'react';
import { Input, Country, PostCode, Radio } from '@/components/fields';
import { useFormContext } from 'react-hook-form';
import { useStateMachine } from 'little-state-machine';
import Address from './Address';
import { useApplication } from '@/hooks/useApplication';
import { useTheme } from '@/contexts/ThemeContext';

export default function AddressDetails(props: {
  setTenancyPeriods: (arg0: any) => void;
  tenancyPeriods: { [key: string]: number };
}): JSX.Element {
  const { landingPage } = useTheme();
  const [showCurrentAddress, setShowCurrentAddress] = useState(false);
  const [currentManualAddress, setCurrentManualAddress] = useState(false);
  const [showLicenceAddress, setShowLicenceAddress] = useState(false);
  const [licenceManualAddress, setLicenceManualAddress] = useState(false);
  const [previousAddresses, setPreviousAddresses] = useState(0);
  const { errors, watch, clearErrors, register, setValue } = useFormContext();
  const { applicationId, data: application } = useApplication(setValue);
  const {
    state: {
      form: { postcode },
    },
  } = useStateMachine();
  const same_as_current_address = watch('same_as_current_address');
  const yearsLived = watch('current_years_lived');
  const monthsLived = watch('current_months_lived');
  const totalMonths =
    parseInt(yearsLived || 0) * 12 + parseInt(monthsLived || 0);

  const isSameAddressAnswered =
    typeof same_as_current_address !== 'undefined' &&
    same_as_current_address != '';
  let sameAsCurrentAddress = false;

  if (isSameAddressAnswered) {
    sameAsCurrentAddress =
      same_as_current_address === 'same_as_current_address_yes';
  }

  const addPreviousAddress = () => {
    setPreviousAddresses(previousAddresses + 1);
  };

  useEffect(() => {
    if (isSameAddressAnswered) clearErrors();
  }, [isSameAddressAnswered]);

  useEffect(() => {
    if (totalMonths > 0) {
      props.setTenancyPeriods({
        ...props.tenancyPeriods,
        current: totalMonths,
      });
    }
  }, [totalMonths]);

  useEffect(() => {
    if (applicationId) {
      setShowLicenceAddress(true);
      setShowCurrentAddress(true);
      setPreviousAddresses(Number(application.addresses_num - 1));
    }
  }, [applicationId, application]);

  return (
    <>
      <div>
        <input
          type="hidden"
          name="addresses_num"
          ref={register}
          value={previousAddresses + 1}
        />
        {landingPage !== 'v12' && landingPage !== 'v13' && (
          <h3 className="bg-box-header text-xl leading-8 font-medium tracking-tight text-white flex items-center mb-5 py-2 px-4">
            Address Details
          </h3>
        )}

        <div className="px-6">
          <div>
            <h4 className="text-xl leading-6 font-medium tracking-tight text-gray-900 flex items-center mb-5 mt-10">
              Address as Displayed on Your Licence
            </h4>
            <PostCode
              id="postcode_lookup_licence"
              isRequired={false}
              defaultValue={postcode}
              setShowAddress={setShowLicenceAddress}
              setManualAddress={setLicenceManualAddress}
            >
              Address Postcode
            </PostCode>
            <div className="flex">
              <div className="md:w-2/5 md:pr-4"></div>
              <div className="md:w-2/5 md:pl-4">
                {errors?.licence_postcode && (
                  <p className="-mt-2 text-sm text-red-600">
                    {errors['licence_postcode'].message}
                  </p>
                )}
              </div>
            </div>
            <div
              className={
                showLicenceAddress || licenceManualAddress ? 'block' : 'hidden'
              }
            >
              <div className="hidden">
                <Input id="licence_building_name_number" isRequired>
                  Building Name/Number
                </Input>
              </div>
              <Input id="licence_street_name" isRequired>
                Address line 1
              </Input>
              <Input id="licence_address_line_2" isRequired={false}>
                Address line 2
              </Input>
              <Input id="licence_town" isRequired>
                Town
              </Input>
              <Input id="licence_postcode" isRequired>
                Postcode
              </Input>
              <Country id="licence_country" isRequired>
                Country
              </Country>
            </div>
          </div>
          <div className="mt-4 md:flex md:items-center">
            <div className="md:w-2/5 md:pr-4">
              <span className="block text-sm leading-5 font-normal text-gray-900">
                Do you live at the same address?
                <sup className="text-red-600 font-sans">*</sup>
              </span>
            </div>
            <div className="md:w-2/5 md:pl-4">
              <div className="flex items-center">
                <Radio
                  name="same_as_current_address"
                  id="same_as_current_address_yes"
                  className="mr-4"
                >
                  Yes
                </Radio>
                <Radio
                  name="same_as_current_address"
                  id="same_as_current_address_no"
                  className="mr-4"
                >
                  No
                </Radio>
              </div>
              {errors['same_as_current_address'] ? (
                <p className="mb-4 font-medium text-sm text-red-600">
                  {errors['same_as_current_address'].message}
                </p>
              ) : null}
            </div>
          </div>
          {isSameAddressAnswered && !sameAsCurrentAddress && (
            <div>
              <h4 className="text-xl leading-6 font-medium tracking-tight text-gray-900 flex items-center mb-5 mt-10">
                Current Address
              </h4>
              <PostCode
                id="postcode_lookup_current"
                isRequired={false}
                setShowAddress={setShowCurrentAddress}
                setManualAddress={setCurrentManualAddress}
              >
                Address Postcode
              </PostCode>
              <div className="flex">
                <div className="md:w-2/5 md:pr-4"></div>
                <div className="md:w-2/5 md:pl-4">
                  {errors?.current_postcode && (
                    <p className="-mt-2 text-sm text-red-600">
                      {errors['current_postcode'].message}
                    </p>
                  )}
                </div>
              </div>
              <div
                className={
                  showCurrentAddress || currentManualAddress
                    ? 'block'
                    : 'hidden'
                }
              >
                <div className="hidden">
                  <Input id="current_building_name_number" isRequired>
                    Building Name/Number
                  </Input>
                </div>
                <Input id="current_street_name" isRequired>
                  Address line 1
                </Input>
                <Input id="current_address_line_2" isRequired={false}>
                  Address line 2
                </Input>
                <Input id="current_town" isRequired>
                  Town
                </Input>
                <Input id="current_postcode" isRequired>
                  Postcode
                </Input>
                <Country id="current_country" isRequired>
                  Country
                </Country>
              </div>
            </div>
          )}
          {isSameAddressAnswered && (
            <div className="mt-4 md:flex md:items-center">
              <div className="md:w-2/5 md:pr-4">
                <p className="text-sm leading-5 font-normal text-gray-900">
                  How long have you lived at current address?{' '}
                  <sup className="text-red-600 font-sans">*</sup>
                  <br />
                  <span className="font-medium text-xs text-red-600">
                    Tenancy years must be more than 3 years
                  </span>
                </p>
              </div>
              <div className="md:w-2/5 md:pl-4">
                <div className="flex justify-between">
                  <div className="w-1/2 pr-2">
                    <Input id="current_years_lived" isRequired position="right">
                      Years
                    </Input>
                  </div>
                  <div className="w-1/2 pl-2">
                    <Input
                      id="current_months_lived"
                      isRequired
                      position="right"
                    >
                      Months
                    </Input>
                  </div>
                </div>
              </div>
            </div>
          )}

          {[...Array(previousAddresses)].map((_, index) => {
            const key = index + 1;

            return (
              <Address
                id={key}
                tenancyPeriods={props.tenancyPeriods}
                setTenancyPeriods={props.setTenancyPeriods}
                key={key}
                previousAddresses={previousAddresses}
                setPreviousAddresses={setPreviousAddresses}
                isAddressShown={!!applicationId}
              />
            );
          })}

          {props.tenancyPeriods.current &&
          Object.values(props.tenancyPeriods).reduce((acc, key) => acc + key) <
            36 ? (
            <div className="mt-0 md:flex md:items-center">
              <div className="md:w-2/5 md:pr-4"></div>
              <div className="md:w-2/5 md:pl-4">
                <span className="text-sm text-red-500">
                  You must complete the full 3 years address history.{' '}
                  <span
                    onClick={addPreviousAddress}
                    className="text-gray-800 cursor-pointer font-semibold"
                  >
                    Add previous address
                  </span>
                </span>
              </div>
            </div>
          ) : null}
        </div>
      </div>
    </>
  );
}
